import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LucideAngularModule, LogIn, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-angular';
import { AuthService, LoginRequest } from '../../../core';
// import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './login-page.component.html',
  styles: [`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }
    .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
    .animate-slideUp { animation: slideUp 0.5s ease-out; }
    .animate-shake { animation: shake 0.3s ease-in-out; }
  `]
})
export class LoginPageComponent {
  loginForm: FormGroup;
  showPassword = signal(false);
  isLoading = signal(false);
  
  errorMessage = signal<string | null>(null);
  returnUrl = signal<string>('/dashboard');

  readonly MailIcon = Mail;
  readonly LockIcon = Lock;
  readonly EyeIcon = Eye;
  readonly EyeOffIcon = EyeOff;
  readonly LogInIcon = LogIn;
  readonly ShieldIcon = Shield;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  togglePassword(): void {
    this.showPassword.update(v => !v);
  }
  ngOnInit(): void {
    // Get return URL from route parameters or default to '/dashboard'
    this.returnUrl.set(this.route.snapshot.queryParams['returnUrl'] || '/dashboard');

    // Clear any previous error messages
    this.errorMessage.set(null);

    if (this.authService.isAuthenticated()) {
      this.router.navigateByUrl(this.resolveRedirectUrl());
    }
  }
   private resolveRedirectUrl(): string {
    // Admin SEMPRE vai para /admin, nunca para dashboard
    if (this.authService.isAdmin()) {
      return '/admin';
    }

    // Para não-admin, usa returnUrl se for válido
    const target = this.returnUrl();
    if (target && target !== '/' && target !== '/dashboard' && target !== '/admin') {
      return target;
    }

    return '/dashboard?company_id='+this.authService.getCurrentUser()?.company_id;
  }

  onSubmit(): void {

    if (this.isLoading()) return;
    if (this.loginForm.invalid) {
      Object.keys(this.loginForm.controls).forEach((key) => {
        this.loginForm.get(key)?.markAsTouched();
      });
      return;
    }
    if (this.loginForm.valid) {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const credentials: LoginRequest = {
        email: this.loginForm.value.email,
        password: this.loginForm.value.password,
      };

      this.authService.login(credentials).subscribe({
        next: () => {
          this.isLoading.set(false);
          this.router.navigateByUrl(this.resolveRedirectUrl());
        },
        error: (error) => {
          this.isLoading.set(false);
          console.error('Login error:', error);
          this.errorMessage.set(
            error?.error?.message || 'Erro ao fazer login. Verifique suas credenciais.',
          );
        },
      });
    }
  }

  forgotPassword(): void {
    this.router.navigate(['/auth/forgot-password']);
  }
}